// src/services/contentPlan.ts
// ============================================
// 自媒体内容计划服务
//
// 功能：
//   1. 读取每日自媒体内容计划（微信公众号/抖音企业号/抖音个人号/小红书/知乎）
//   2. 关联知识库主题
//   3. 提供待生成内容队列
//   4. 生成状态追踪
//
// 数据来源：
//   - Obsidian Vault: 10_Operations（运营层）/Content/每日自媒体计划-YYYY-MM-DD.md
//   - 降级：从今日计划自动派生
// ============================================

/** 自媒体平台 */
export type SocialPlatform =
  | 'wechat' | 'douyin_enterprise' | 'douyin_personal' | 'xiaohongshu'
  | 'zhihu' | 'bilibili' | 'live_stream'
  // v6.1：微信生态高粘性渠道（视频号短视频 + 朋友圈短文案）
  | 'wechat_channels' | 'wechat_moments'
  // v6.3.19：今日头条（图文/微头条，信息流算法分发，标题权重高）
  | 'toutiao'
  // v6.6：个人订阅号「小舍AI」热度号（不走主链路，手动发布，选题匹配全网热点）
  | 'wechat_subscription'
  // v6.7：CSDN（技术博客，深度长文，Markdown 结构，SEO 友好，AI 高引用，GEO 核心信源）
  | 'csdn';

/** 内容计划条目 */
export interface ContentPlanItem {
  /** 条目 ID */
  id: string;
  /** 平台 */
  platform: SocialPlatform;
  /** 主题 */
  topic: string;
  /** 关联知识库笔记路径 */
  knowledgeNote?: string;
  /** 内容类型（文章/视频/图文/直播） */
  contentType: 'article' | 'video' | 'image_text' | 'live';
  /** 生成状态 */
  status: 'pending' | 'generating' | 'completed' | 'failed' | 'published';
  /** 关联的媒体包 ID */
  packageId?: string;
  /** v6.1 知乎问答模式：article=专栏文章（默认）/ qa=回答用户问题 */
  itemType?: 'article' | 'qa';
  /** v6.1 知乎问答模式的问题（itemType==='qa' 时生效，生成时按问题回答） */
  question?: string;
  /** 计划日期 */
  date: string;
  /** 优先级 */
  priority: 'high' | 'medium' | 'low';
  /** 备注 */
  note?: string;
}

/** 平台显示配置 */
export const PLATFORM_CONFIG: Record<SocialPlatform, { name: string; icon: string; color: string; autoPublish: boolean }> = {
  wechat: { name: '微信公众号', icon: '📱', color: '#07C160', autoPublish: true },
  douyin_enterprise: { name: '抖音企业号', icon: '🎬', color: '#000000', autoPublish: false },
  xiaohongshu: { name: '小红书', icon: '📕', color: '#FF2442', autoPublish: false },
  zhihu: { name: '知乎', icon: '💡', color: '#0084FF', autoPublish: false },
  douyin_personal: { name: '抖音个人号', icon: '🎭', color: '#000000', autoPublish: false },
  // v6.3.19 今日头条：图文文章为主，信息流算法分发，标题/首图权重高（位置紧跟抖音个人号）
  toutiao: { name: '今日头条', icon: '📰', color: '#FF4D4D', autoPublish: false },
  bilibili: { name: 'B站', icon: '📺', color: '#FB7299', autoPublish: false },
  live_stream: { name: '每日直播', icon: '🎥', color: '#FF6B6B', autoPublish: false },
  // v6.1 微信生态：视频号（短视频）+ 朋友圈（短文案），微信私域粘性高
  wechat_channels: { name: '微信视频号', icon: '📹', color: '#3E7FFF', autoPublish: false },
  wechat_moments: { name: '微信朋友圈', icon: '💬', color: '#07C160', autoPublish: false },
  // v6.6 个人订阅号「小舍AI」热度号：独立副业号，不走自动发布（手动复制发布），选题匹配全网热点
  wechat_subscription: { name: '订阅号小舍AI', icon: '📰', color: '#07C160', autoPublish: false },
  // v6.7 CSDN：技术博客（深度长文，Markdown 结构，SEO 友好，AI 高引用，GEO 核心信源；无开放自动发布 API → 手动发布）
  csdn: { name: 'CSDN', icon: '💻', color: '#0084FF', autoPublish: false },
};

/**
 * 内容计划服务
 */
export class ContentPlanService {
  private logger: Console;

  constructor(logger: Console = console) {
    this.logger = logger;
  }

  /**
   * 获取今日内容计划
   *
   * 优先级：
   *   1. 读取 Obsidian 每日自媒体计划文件
   *   2. 关联每日动态：从今日 Hermes 新闻摘要派生主题
   *   3. 降级：从今日日计划派生
   *   4. 降级：自动生成（各平台各 2 篇 + B站/直播各 1 篇）
   */
  async getTodayPlan(): Promise<ContentPlanItem[]> {
    const today = new Date().toISOString().slice(0, 10);

    // 1. 尝试读取自媒体计划文件
    const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${today}.md`;
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(planPath)}`);
      if (resp.ok) {
        const result = await resp.json();
        if (result.success && result.content) {
          const parsed = this.parsePlanFromMarkdown(result.content, today);
          if (parsed.length > 0) return parsed;
        }
      }
    } catch {
      // 降级
    }

    // 2. 关联每日动态：从今日 Hermes 新闻摘要派生内容计划主题
    //    将当日 AI 新闻热点作为各平台的内容生成主题来源
    try {
      const newsPlan = await this.derivePlanFromNewsDigest(today);
      if (newsPlan.length > 0) {
        console.log(`[ContentPlanService] 从每日动态派生 ${newsPlan.length} 条计划`);
        return newsPlan;
      }
    } catch (err) {
      console.warn('[ContentPlanService] 从每日动态派生失败:', err);
    }

    // 3. 从今日日计划派生
    const dailyPlanPath = `10_Operations（运营层）/Daily/${today}.md`;
    try {
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(dailyPlanPath)}`);
      if (resp.ok) {
        const result = await resp.json();
        if (result.success && result.content) {
          return this.derivePlanFromDaily(result.content, today);
        }
      }
    } catch {
      // 降级
    }

    // 4. 自动生成今日计划（各平台各 2 篇 + B站/直播各 1 篇）并写入 Vault
    //    替代原来的硬编码 getDefaultPlan，确保首次使用即有完整的结构化计划
    try {
      console.log('[ContentPlanService] 自媒体计划和日计划文件均不存在，自动生成各平台各2篇计划');
      return await this.generateTodayPlan();
    } catch (err) {
      console.warn('[ContentPlanService] 自动生成计划失败，降级到默认模板:', err);
      return this.getDefaultPlan(today);
    }
  }

  /**
   * v5.2 按主题重写今日自媒体计划（用户诉求：自动选题不合适时，全部围绕某类主题写）
   *
   * 例如 topicHint="XEAOS" → 从 Obsidian 知识库筛选所有含 "XEAOS" 的笔记，
   * 提取主题后按平台分配（公众号/抖音企业号/小红书/知乎/抖音个人号/B站/直播），
   * 生成一份"全部围绕该主题"的计划，并覆盖写入计划文件。
   *
   * @param topicHint 主题提示词（如 "XEAOS"、"酒店"、"量化"）
   * @returns 新计划条目；无匹配内容时返回空数组
   */
  async regeneratePlanByTopic(topicHint: string): Promise<ContentPlanItem[]> {
    const today = new Date().toISOString().slice(0, 10);
    const hint = (topicHint || '').trim();
    if (!hint) {
      this.logger.warn?.('[ContentPlanService] 主题提示为空');
      return [];
    }

    // 1. 扫描知识库 + XEAOS 业务目录，收集含主题提示的文件
    // v6.31 加入 HotTopics 目录：用户输入热点标题（如"2026头条变现真相"）也能匹配到热点库，
    // 否则热点标题在知识库无匹配 → unique 为空 → 计划不变（用户反馈"按主题重写后标题不变"根因之一）
    const knowledgeDirs = [
      '09_Knowledge（知识中心）',
      '09_Knowledge（知识层）',
      '09_Knowledge',
      'XEAOS/Project',
      '11_Strategy（战略层）',
      '10_Operations（运营层）/Daily',
      '10_Operations（运营层）/Journal',
      '10_Operations（运营层）/Reviews',
      '10_Operations（运营层）/Content/HotTopics',
    ];
    let notes: Array<{ path: string; name: string }> = [];
    for (const dir of knowledgeDirs) {
      try {
        const resp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir)}&ext=.md`);
        if (resp.ok) {
          const result = await resp.json();
          if (result.success && Array.isArray(result.files)) {
            const dirNotes = result.files
              .filter((f: any) => !f.isDirectory)
              .map((f: any) => ({ path: f.path, name: f.name }));
            // HotTopics 目录全部纳入（内容才是主题来源，文件名是"今日热点-日期"不含关键词）；
            // 其余目录只纳入文件名含主题提示的（后续再按内容二次筛选）
            if (dir.includes('HotTopics')) {
              notes = [...notes, ...dirNotes];
            } else {
              notes = [...notes, ...dirNotes.filter((n: { name: string }) => n.name.includes(hint))];
            }
          }
        }
      } catch {
        // 目录不存在或读取失败，跳过
      }
    }

    // 2. 读取内容进一步筛选（文件名不含但内容含主题的也纳入）并提取主题
    const topics: Array<{ topic: string; notePath: string }> = [];
    const readPromises = notes.slice(0, 25).map(async (note) => {
      try {
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(note.path)}`);
        if (!resp.ok) return;
        const result = await resp.json();
        if (!result.success || !result.content) return;
        const content = String(result.content);
        if (!content.includes(hint)) return;
        // 特例：选题库文件（含 - [ ] 选题行）→ 提取内部选题作为多个主题
        // 这样"按主题重写 XEAOS"会把选题库里的 20+ 个选题方向展开成计划主题
        if (note.name.includes('选题库')) {
          const poolTopics = content.match(/^\s*- \[ \]\s*(.+)$/gm) || [];
          for (const t of poolTopics) {
            const clean = t.replace(/^\s*- \[ \]\s*/, '').trim().replace(/\s*（[^）]*）\s*$/, '');
            if (clean.length >= 6 && clean.length <= 60) {
              topics.push({ topic: clean, notePath: note.path });
            }
          }
          return;
        }
        // v6.31 特例：今日热点文件（格式 `- 标题 | 适配角度`）→ 提取每条热点标题作为主题
        // 用户输入热点标题关键词（如"赚钱路子/头条变现"）即可匹配到具体热点选题
        if (note.name.includes('今日热点')) {
          const hotLines = content.match(/^\s*-\s+(.+?)\s*(?:\||$)/gm) || [];
          for (const ln of hotLines) {
            if (ln.includes('敏感热点') || ln.includes('禁止发文')) continue; // 跳过敏感区块
            const t = ln.replace(/^\s*-\s+/, '').split('|')[0].trim();
            if (t.length >= 4 && t.length <= 60) {
              topics.push({ topic: t, notePath: note.path });
            }
          }
          return;
        }
        // 提取主题：标题行优先，其次 frontmatter name，最后文件名
        const heading = content.match(/^#\s+(.+?)$/m)?.[1]?.trim();
        const name = content.match(/^name:\s*(.+?)$/m)?.[1]?.trim().replace(/^["']|["']$/g, '');
        const topic = heading || name || note.name.replace(/\.md$/, '').trim();
        if (topic && topic.length >= 2) {
          topics.push({ topic: topic.slice(0, 60), notePath: note.path });
        }
      } catch {
        // 单篇读取失败跳过
      }
    });
    await Promise.all(readPromises);

    // 3. 去重 + 排序（标题含主题的优先）
    const seen = new Set<string>();
    const unique = topics.filter((t) => {
      if (seen.has(t.topic)) return false;
      seen.add(t.topic);
      return true;
    });
    unique.sort((a, b) => {
      const score = (t: string) => (t.includes(hint) ? 0 : 1);
      return score(a.topic) - score(b.topic);
    });

    if (unique.length === 0) {
      // v6.31 修复：知识库无匹配时不再返回空（原逻辑 → 前端计划原样不动，用户反馈"标题不变"），
      // 直接用用户输入的主题兜底——用户诉求"我要用标题写一篇今日头条的文章"：输入即选题
      this.logger.log?.(`[ContentPlanService] 知识库无「${hint}」匹配，直接用输入主题兜底`);
      unique.push({ topic: hint.slice(0, 60), notePath: '' });
    }

    // 4. 按平台分配主题（各平台 2 篇，B站/直播 1 篇）
    // v6.1.10 统一分配：循环分配 + 不足时共用主题，保证 9 个平台全部有内容
    const platformOrder: SocialPlatform[] = [
      'wechat', 'wechat_subscription', 'douyin_enterprise', 'xiaohongshu', 'zhihu', 'csdn', 'douyin_personal', 'toutiao', 'bilibili', 'live_stream',
      'wechat_channels', 'wechat_moments',
    ];
    const items = this.allocateTopics(
      today,
      platformOrder,
      unique.map((u) => u.topic),
      undefined,
      unique.map((u) => u.notePath)
    );

    // 5. 覆盖写入计划文件
    if (items.length > 0) {
      const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${today}.md`;
      const markdown = this.serializePlanToMarkdown(items, today);
      try {
        await fetch(`/vault-api/write?path=${encodeURIComponent(planPath)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: markdown }),
        });
      } catch (err) {
        this.logger.warn?.('[ContentPlanService] 按主题重写后写入 Vault 失败', err);
      }
    }

    this.logger.log?.(`[ContentPlanService] 已按主题「${hint}」重写计划，共 ${items.length} 条`);
    return items;
  }

  /**
   * 关联每日动态：从今日 Hermes 新闻摘要派生内容计划
   *
   * 读取 Obsidian Vault 中 09_Knowledge/News/ 目录下当日新闻摘要，
   * 提取 topStories 标题作为各平台的内容生成主题。
   * 每个平台 2 篇主题（B站 1 篇），主题来自真实当日 AI 新闻热点。
   */
  private async derivePlanFromNewsDigest(date: string): Promise<ContentPlanItem[]> {
    // 尝试多个可能的新闻摘要路径
    // 修复①：NewsRuntime 实际写入路径为 XEAOS/Daily/News-{date}.md（writeXeaosDaily），
    // 旧路径 09_Knowledge（知识层）/News/ 从未命中 → 派生恒为空 → 刷新回退静态知识库
    // 修复②：兼容历史无扩展名文件（旧版 writeXeaosDaily 漏写 .md，如 News-2026-08-06）
    const possiblePaths = [
      `XEAOS/Daily/News-${date}.md`,
      `XEAOS/Daily/News-${date}`,
      `XEAOS/Daily/news-digest-${date}.md`,
      `XEAOS/Daily/news-digest-${date}`,
      `09_Knowledge（知识层）/News/AI新闻摘要-${date}.md`,
      `09_Knowledge/News/AI新闻摘要-${date}.md`,
    ];

    let newsContent = '';
    for (const p of possiblePaths) {
      try {
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(p)}`);
        if (resp.ok) {
          const result = await resp.json();
          if (result.success && result.content) {
            newsContent = result.content;
            break;
          }
        }
      } catch {
        // 继续尝试下一个路径
      }
    }

    if (!newsContent) return [];

    // 从新闻摘要 Markdown 中提取热点标题
    // NewsDigestBuilder.toMarkdown 的 Top Stories 是表格格式：
    //   | 1 | 8/10 | OpenAI | [标题](url) | 一句话总结 |
    // 商业机会是：### 🔴 #1 标题
    // 注意：不做通用 ## 标题/列表兜底，避免把"执行摘要/Top Stories"等固定段落名当主题
    const titles: string[] = [];
    const lines = newsContent.split('\n');
    for (const line of lines) {
      // ① Top Stories 表格行（真实新闻标题，每日更新）
      const m1 = line.match(/^\|\s*\d+\s*\|\s*\d+\/10\s*\|\s*[^|]*\|\s*\[([^\]]+)\]\([^)]*\)\s*\|/);
      // ② 商业机会标题：### 🔴 #1 xxx
      const m2 = line.match(/^###\s+.{0,4}#\d+\s+(.+?)\s*$/);
      const title = (m1?.[1] || m2?.[1] || '').trim();
      if (title && title.length >= 6 && title.length <= 80 && !titles.includes(title)) {
        titles.push(title);
      }
      if (titles.length >= 20) break; // 最多 20 个主题（v6 每平台 3 篇）
    }

    if (titles.length === 0) return [];

    // v6.1.9 修复：新闻标题通常只有 10-20 个，9 个平台需要 25 个主题 →
    // 标题在知乎（第 4 平台）就用完，抖音个人号/视频号/朋友圈分不到主题（用户反馈"没标题没内容"）。
    // 修复①：主题池不足时，用知识库主题补齐到 25 个候选。
    if (titles.length < 25) {
      try {
        const kbItems = await this.buildTodayPlanItems(date);
        for (const it of kbItems) {
          if (titles.length >= 25) break;
          if (!titles.includes(it.topic)) titles.push(it.topic);
        }
      } catch { /* 知识库兜底失败忽略 */ }
    }

    // 随机打乱主题池（Fix：知识库/新闻主题未更新时，刷新后各平台仍能拿到不同标题）
    for (let i = titles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [titles[i], titles[j]] = [titles[j], titles[i]];
    }

    // v6.1.10 统一分配：循环分配 + 不足时共用主题，保证 9 个平台全部有内容
    const platformOrder: SocialPlatform[] = [
      'wechat', 'wechat_subscription', 'douyin_enterprise', 'xiaohongshu', 'zhihu', 'csdn', 'douyin_personal', 'toutiao', 'bilibili', 'live_stream',
      'wechat_channels', 'wechat_moments',
    ];
    const items = this.allocateTopics(date, platformOrder, titles, '关联每日动态：今日 AI 新闻热点');

    // 写入 Vault 以便后续读取
    if (items.length > 0) {
      const markdown = this.serializePlanToMarkdown(items, date);
      const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${date}.md`;
      try {
        // 注意：vite 中间件要求 path 在 query 参数中，content 在 body 中
        await fetch(`/vault-api/write?path=${encodeURIComponent(planPath)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ content: markdown }),
        });
      } catch {
        // 写入失败不阻塞
      }
    }

    return items;
  }

  /**
   * 生成今日内容计划（各平台各 2 篇 + B站/直播各 1 篇）
   *
   * 生成结构化计划并写入 Obsidian Vault：
   *   - 微信公众号：2 篇
   *   - 抖音企业号：2 篇
   *   - 抖音个人号：2 篇
   *   - 小红书：2 篇
   *   - 知乎：2 篇
   *   - B站：1 篇（可选）
   *   - 每日直播：1 篇（1-2 小时直播脚本）
   *
   * 写入 Vault 失败不阻塞，仍然返回生成的计划。
   */
  async generateTodayPlan(): Promise<ContentPlanItem[]> {
    const today = new Date().toISOString().slice(0, 10);
    // buildTodayPlanItems 已改为异步（需读取 Obsidian 知识库），此处必须 await
    const items = await this.buildTodayPlanItems(today);

    // 写入 Vault：POST /vault-api/write?path=xxx body={content}
    // 注意：vite 中间件要求 path 在 query 参数中，content 在 body 中
    const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${today}.md`;
    const markdown = this.serializePlanToMarkdown(items, today);
    try {
      await fetch(`/vault-api/write?path=${encodeURIComponent(planPath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: markdown }),
      });
    } catch (err) {
      // 写入失败不阻塞，仍然返回生成的计划
      this.logger.warn?.('[ContentPlanService] 写入 Vault 失败，仍返回生成的计划', err);
    }

    return items;
  }

  /**
   * 手动刷新今日计划
   *
   * 强制从 Obsidian 知识库重新读取当日笔记与知识层内容，
   * 基于真实笔记内容生成今日计划，并写入 Vault 覆盖旧计划。
   *
   * 与 getTodayPlan 的区别：
   *   - getTodayPlan 优先读取已有计划文件，仅在降级时才生成
   *   - refreshTodayPlan 强制重新生成，跳过已有计划文件
   *
   * @returns 生成的今日计划条目；若 Obsidian 知识库无可读内容则返回空数组
   */
  async refreshTodayPlan(): Promise<ContentPlanItem[]> {
    const today = new Date().toISOString().slice(0, 10);
    // 1. 优先从今日动态（AI 新闻热点）派生——每日新闻不同，保证"刷新出新的平台主题/标题"
    //    修复：此前直接 buildTodayPlanItems（静态知识库笔记标题），知识库不变 → 小红书等平台
    //    主题永远相同，用户反馈"刷新后内容还是和昨天一样"。
    let items: ContentPlanItem[] = [];
    try {
      items = await this.derivePlanFromNewsDigest(today);
      if (items.length > 0) {
        this.logger.log?.('[ContentPlanService] 刷新：基于今日动态新闻热点派生（每日更新）');
      }
    } catch (err) {
      this.logger.warn?.('[ContentPlanService] 今日动态派生失败:', err);
    }
    // 2. 无当日新闻 → 从 Obsidian 知识库构建
    if (items.length === 0) {
      items = await this.buildTodayPlanItems(today);
    }
    // 3. 兜底：从今日日计划派生
    if (items.length === 0) {
      try {
        const dailyPlanPath = `10_Operations（运营层）/Daily/${today}.md`;
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(dailyPlanPath)}`);
        if (resp.ok) {
          const result = await resp.json();
          if (result.success && result.content) {
            items = this.derivePlanFromDaily(result.content, today);
          }
        }
      } catch {
        // 忽略
      }
    }
    if (items.length === 0) {
      this.logger.warn?.('[ContentPlanService] 刷新失败：Obsidian 知识库无可读内容');
      return [];
    }
    // ★ 公众号为微信服务号，每月仅 4 次群发额度 → 固定周一发布，避免额度浪费
    // 非周一从计划剔除公众号条目（其他平台不受影响），确保自动发布只在周一触发
    const isMonday = new Date().getDay() === 1;
    if (!isMonday) {
      const removedCount = items.filter((it) => it.platform === 'wechat').length;
      items = items.filter((it) => it.platform !== 'wechat');
      if (removedCount > 0) {
        this.logger.log?.(`[ContentPlanService] 非周一，已剔除 ${removedCount} 条公众号计划（服务号仅周一发布）`);
      }
    }
    // ★ 订阅号「小舍AI」热度号（v6.6）+ 今日头条（v6.31）：选题匹配全网热点——
    // 从《Content/HotTopics/今日热点-{date}.md》热点库取热点。用户实测定位（2026-08-15）：
    //   今日头条 = 网络热点类（信息流，标题/首图权重高，追热点）
    //   订阅号小舍AI = 热度号（创始人第一人称观点文，追热点）
    //   小红书 = 经验类（OPC/创业/避坑经验，见 buildTodayPlanItems 经验素材优先）
    // 找不到热点库则保留默认主题并标注提示（订阅号不走自动发布，手动复制发布）
    const hotItems = items.filter((it) => it.platform === 'wechat_subscription' || it.platform === 'toutiao');
    if (hotItems.length > 0) {
      try {
        const hotResp = await fetch(`/vault-api/read?path=${encodeURIComponent(`10_Operations（运营层）/Content/HotTopics/今日热点-${today}.md`)}`);
        if (hotResp.ok) {
          const hotResult = await hotResp.json();
          if (hotResult.success && hotResult.content) {
            const hotTitles: string[] = [];
            for (const line of hotResult.content.split('\n')) {
              // 热点库格式：`- 热点标题 | 适配角度`（只取 | 前标题）
              const m = line.match(/^\s*-\s+([^|]+?)\s*(?:\||$)/);
              const t = m?.[1]?.trim() || '';
              if (t && t.length >= 4 && t.length <= 60 && !hotTitles.includes(t)) {
                hotTitles.push(t);
              }
            }
            if (hotTitles.length > 0) {
              hotItems.forEach((it, idx) => {
                const hot = hotTitles[idx % hotTitles.length];
                it.topic = `热点速评：${hot}`;
                it.note = `热度号选题：${hotTitles.slice(0, 5).join(' / ')}（详见热点库 ${today}）`;
                it.priority = 'high';
              });
              this.logger.log?.(`[ContentPlanService] 订阅号/今日头条已匹配 ${hotTitles.length} 个热点选题`);
            }
          }
        }
      } catch {
        // 热点库读取失败则保留默认主题
      }
    }
    // 4. 写入 Vault 覆盖旧计划文件
    const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${today}.md`;
    const markdown = this.serializePlanToMarkdown(items, today);
    try {
      // 注意：vite 中间件要求 path 在 query 参数中，content 在 body 中
      await fetch(`/vault-api/write?path=${encodeURIComponent(planPath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: markdown }),
      });
    } catch (err) {
      // 写入失败不阻塞，仍然返回生成的计划
      this.logger.warn?.('[ContentPlanService] 刷新后写入 Vault 失败，仍返回生成的计划', err);
    }

    this.logger.log?.(`[ContentPlanService] 已刷新今日计划，共 ${items.length} 条`);
    return items;
  }

  /**
   * 构建今日计划条目
   *
   * 严格基于 Obsidian 知识库的真实笔记内容生成计划：
   *   1. 通过 /vault-api/list 列出知识层目录下的笔记
   *   2. 读取笔记内容，提取标题作为主题
   *   3. 按平台分配主题（各平台 2 篇，B站/直播各 1 篇）
   *
   * 若知识库为空或读取失败，返回空数组（绝不凭空生成）。
   */
  private async buildTodayPlanItems(date: string): Promise<ContentPlanItem[]> {
    // 1. 通过 /vault-api/list 读取 Obsidian 知识库目录
    const knowledgeDirs = [
      '09_Knowledge（知识中心）',
      '09_Knowledge（知识层）',
      '09_Knowledge',
    ];

    let notes: Array<{ path: string; name: string }> = [];
    for (const dir of knowledgeDirs) {
      try {
        const resp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir)}&ext=.md`);
        if (resp.ok) {
          const result = await resp.json();
          if (result.success && Array.isArray(result.files)) {
            notes = result.files
              .filter((f: any) => !f.isDirectory)
              .map((f: any) => ({ path: f.path, name: f.name }));
            if (notes.length > 0) break;
          }
        }
      } catch {
        // 继续尝试下一个目录
      }
    }

    // 额外读取 XEAOS 业务内容（项目/战略/日计划），确保每日计划基于企业实际业务
    // v6.31 加入 Journal 经验素材目录：用户实测定位"小红书=经验类"，
    // XEAOS经验-*.md（MVP方法论/定价商业化/故障复盘等）进入主题池 → 小红书有经验类选题可选
    const xeaosDirs = [
      'XEAOS/Project',
      '11_Strategy（战略层）',
      '10_Operations（运营层）/Daily',
      '10_Operations（运营层）/Journal',
    ];
    for (const dir of xeaosDirs) {
      try {
        const resp = await fetch(`/vault-api/list?dir=${encodeURIComponent(dir)}&ext=.md`);
        if (resp.ok) {
          const result = await resp.json();
          if (result.success && Array.isArray(result.files)) {
            const xeaosNotes = result.files
              .filter((f: any) => !f.isDirectory)
              .map((f: any) => ({ path: f.path, name: f.name }));
            notes = [...notes, ...xeaosNotes];
          }
        }
      } catch {
        // 继续尝试下一个目录
      }
    }

    if (notes.length === 0) {
      this.logger.warn?.('[ContentPlanService] Obsidian 知识库为空或不可达，无法生成计划');
      return [];
    }

    // 2. 读取笔记内容并提取主题（最多读取 20 篇，避免请求过多；最多提取 12 个主题）
    const topics: Array<{ topic: string; notePath: string }> = [];
    const notesToRead = notes.slice(0, 30);
    for (const note of notesToRead) {
      if (topics.length >= 20) break;
      try {
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(note.path)}`);
        if (!resp.ok) continue;
        const result = await resp.json();
        if (!result.success || !result.content) continue;
        // 从笔记内容提取主题
        const extracted = this.extractTopicsFromNote(result.content, note.name);
        for (const topic of extracted) {
          if (topics.length >= 20) break;
          if (!topics.some(t => t.topic === topic)) {
            topics.push({ topic, notePath: note.path });
          }
        }
      } catch {
        // 继续读取下一篇笔记
      }
    }

    if (topics.length === 0) {
      this.logger.warn?.('[ContentPlanService] 知识库笔记无可提取主题，无法生成计划');
      return [];
    }

    // 随机打乱主题池（Fix：知识库未更新时，刷新后各平台仍能拿到不同标题）
    for (let i = topics.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [topics[i], topics[j]] = [topics[j], topics[i]];
    }

    // 3. 按平台分配主题（各平台 2 篇，B站/直播各 1 篇）
    // v6.1.10 统一分配：循环分配 + 不足时共用主题，保证 9 个平台全部有内容
    const platformOrder: SocialPlatform[] = [
      'wechat', 'wechat_subscription', 'douyin_enterprise', 'xiaohongshu', 'zhihu', 'csdn', 'douyin_personal', 'toutiao', 'bilibili', 'live_stream',
      'wechat_channels', 'wechat_moments',
    ];
    const items = this.allocateTopics(
      date,
      platformOrder,
      topics.map((t) => t.topic),
      undefined,
      topics.map((t) => t.notePath)
    );

    return items;
  }

  /**
   * 从笔记内容中提取主题
   *
   * 优先提取 Markdown 一级/二级/三级标题作为主题；
   * 若笔记无标题，使用文件名（去除扩展名）作为主题兜底。
   *
   * @param content 笔记 Markdown 内容
   * @param filename 笔记文件名（含扩展名）
   * @returns 提取到的主题数组（最多 3 个）
   */
  private extractTopicsFromNote(content: string, filename: string): string[] {
    const topics: string[] = [];
    // 优先从 frontmatter 提取项目名称（XEAOS项目文件格式）
    const fmMatch = content.match(/^---[\s\S]*?name:\s*(.+?)\s*$/m);
    if (fmMatch) {
      const projectName = fmMatch[1].trim().replace(/^["']|["']$/g, '');
      if (projectName.length >= 2 && projectName.length <= 80) {
        topics.push(projectName);
      }
    }
    const lines = content.split('\n');
    for (const line of lines) {
      const m = line.match(/^#{1,3}\s+(.+?)$/);
      const title = (m?.[1] || '').trim();
      // 过滤过短/过长的标题，避免无效主题
      if (title && title.length >= 6 && title.length <= 80 && !topics.includes(title)) {
        topics.push(title);
      }
      if (topics.length >= 3) break;
    }
    // 若未提取到标题，使用文件名作为主题兜底
    if (topics.length === 0) {
      const name = filename.replace(/\.md$/, '').trim();
      if (name.length >= 2 && name.length <= 80) {
        topics.push(name);
      }
    }
    return topics;
  }

  /** 序列化计划为 Markdown（兼容 parsePlanFromMarkdown 解析格式）
   *
   * 持久化 status 和 packageId，让切换页面再返回时生成状态不丢失。
   * 格式：- [平台] 主题（关联：xxx.md）{status:completed,packageId:xxx}
   */
  private serializePlanToMarkdown(items: ContentPlanItem[], date: string): string {
    const lines: string[] = [];
    lines.push(`# 每日自媒体计划 - ${date}`);
    lines.push('');
    lines.push(`> 生成时间：${new Date().toISOString()}`);
    lines.push('');

    for (const item of items) {
      const cfg = PLATFORM_CONFIG[item.platform];
      const knowledge = item.knowledgeNote ? `（关联：${item.knowledgeNote}）` : '';
      // v6.1 知乎问答模式：itemType/question 一并持久化（type:qa,question:编码后的问题）
      const qaMeta = item.itemType
        ? `,type:${item.itemType}${item.question ? `,question:${encodeURIComponent(item.question)}` : ''}`
        : '';
      // 持久化运行时状态：status 和 packageId（用花括号包裹，解析时提取）
      const meta = item.status !== 'pending' || item.packageId || qaMeta
        ? ` {status:${item.status}${item.packageId ? `,packageId:${item.packageId}` : ''}${qaMeta}}`
        : '';
      lines.push(`- [${cfg.name}] ${item.topic}${knowledge}${meta}`);
    }

    return lines.join('\n');
  }

  /**
   * 将计划项生成/发布状态写回 Obsidian 计划文件（白皮书 Rule 05 WriteBack）
   *
   * 根因修复：此前 status 只存在前端内存，刷新即回"待生成"（用户反馈的痛点）。
   * 现在生成/发布成功后调用本方法，把 {status:xxx,packageId:yyy} 写回
   * `10_Operations（运营层）/Content/每日自媒体计划-{date}.md`，
   * 刷新后 parsePlanFromMarkdown 直接从文件恢复"已生成/已发布"状态。
   */
  /**
   * 将计划项生成/发布状态写回 Obsidian 计划文件（白皮书 Rule 05 WriteBack）
   *
   * 根因修复：此前 status 只存在前端内存，刷新即回"待生成"（用户反馈的痛点）。
   * 现在生成/发布成功后调用本方法，把 {status:xxx,packageId:yyy} 写回
   * `10_Operations（运营层）/Content/每日自媒体计划-{date}.md`，
   * 刷新后 parsePlanFromMarkdown 直接从文件恢复"已生成/已发布"状态。
   *
   * v6.2.29 修复：矩阵模式下（每个平台都有全部 10 个标题，同 topic 跨 9 平台存在），
   * 旧实现只按 topic 匹配 → 生成一个平台的某条时，会把**所有平台同名 topic 的条目**
   * 全部标记成同一个 packageId（服务器铁证：9 平台第一条都是 script-douyin_personal-xxx）→
   * 用户看到"所有品牌内容都一样"。改为按 platform + topic 双重匹配，只更新目标平台那条。
   *
   * @param platform 目标平台（v6.2.29 新增：区分同名 topic 属于哪个平台）
   */
  async updatePlanItemStatus(
    date: string,
    platform: SocialPlatform,
    topic: string,
    status: ContentPlanItem['status'],
    packageId?: string
  ): Promise<boolean> {
    try {
      const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${date}.md`;
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(planPath)}`);
      if (!resp.ok) return false;
      const result = await resp.json();
      if (!result.success || !result.content) return false;

      // v6.2.29 目标平台显示名（用于匹配计划文件里的 `[平台名]` 前缀）
      const platformName = PLATFORM_CONFIG[platform]?.name || platform;

      const lines = String(result.content).split('\n');
      let updated = false;
      const nextLines = lines.map((line) => {
        // 匹配：- [平台] 主题（关联：xxx.md）{meta}
        const match = line.match(/^-\s*\[([^\]]+)\]\s*(.+?)(?:（关联：(.+?)）)?(?:\s*\{(.+?)\})?$/);
        if (!match) return line;
        // v6.2.29 平台必须一致（矩阵模式下同 topic 跨平台存在，只更新目标平台那条）
        if (match[1].trim() !== platformName) return line;
        const lineTopic = match[2].trim();
        // topic 匹配：完全相等或包含关系（与前端回填逻辑一致）
        const hit = lineTopic === topic || (lineTopic.includes(topic) && topic.length >= 4) || (topic.includes(lineTopic) && lineTopic.length >= 4);
        if (!hit) return line;

        updated = true;
        const knowledge = match[3] ? `（关联：${match[3]}）` : '';
        const meta = ` {status:${status}${packageId ? `,packageId:${packageId}` : ''}}`;
        return `- [${match[1].trim()}] ${lineTopic}${knowledge}${meta}`;
      });
      if (!updated) return false;

      const writeResp = await fetch(`/vault-api/write?path=${encodeURIComponent(planPath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: nextLines.join('\n') }),
      });
      const wr = await writeResp.json();
      return wr.success === true;
    } catch (err) {
      this.logger.warn('[ContentPlanService] 状态写回失败:', err);
      return false;
    }
  }

  /**
   * v6.2.7 从周度大纲详细内容生成每日自媒体计划（闭环：标题大纲 → 详细内容 → 各平台分发）
   *
   * 用户诉求：内容大纲和详细内容已确定后，把它配到每日自媒体计划里，
   * 各品牌平台（小红书/抖音企业号/抖音个人号等）基于同一份详细内容、
   * 按各自的平台规则差异化生成 —— 内容主体不变，只按平台规则适配。
   *
   * 实现：
   *   1. 解析详细内容中的【标题】（v6.2.6 第二层生成的格式；v6.2.17 起为【标题】，
   *      兼容旧格式【口播标题】），无标题时用分节标题（##/###）兜底
   *   2. 复用 allocateTopics 把主题分配到 9 个平台（各平台 3 篇，B站/直播 2 篇）
   *   3. 每条目 knowledgeNote 指向详细内容文件路径（生成内容时按平台规则读取适配）
   *   4. 覆盖写入每日自媒体计划文件
   *
   * @param date 计划日期 YYYY-MM-DD
   * @param detailContent 周度大纲详细内容（markdown）
   * @param detailPath 详细内容文件路径（作为关联，供生成时读取）
   * @returns 新计划条目；解析不到主题时返回空数组
   */
  async regeneratePlanFromWeeklyDetail(
    date: string,
    detailContent: string,
    detailPath: string,
    outlineContent?: string
  ): Promise<ContentPlanItem[]> {
    const content = (detailContent || '').trim();
    if (!content) {
      this.logger.warn?.('[ContentPlanService] 周度大纲详细内容为空，无法生成计划');
      return [];
    }

    // 1. 解析主题（v6.2.28 优先级：周度大纲原始标题 > 详细版【标题】 > 分节兜底）
    // 用户诉求（2026-08-07）：配到每日计划的标题必须与周度大纲一致（"别瞎选！…"原版），
    // 不要 Hermes 自由改写的发布标题。所以最可靠的数据源是【周度大纲文件本身】
    // （结构化 `- 主题1：xxx`），而不是详细版（Hermes 自由输出，格式每次都可能变：
    //   `### 主题1：【...】` / `### 主题1`换行`【标题】xxx` / `- **标题**：xxx` 都出现过——
    //   解析正则永远在打地鼠。从源头周度大纲解析，格式稳定）。
    const topics: string[] = [];

    // A. 【最优先】周度大纲原始标题：`- 主题1：xxx` / `- 主题2: xxx` / `- 主题1 xxx`
    const outlineSrc = (outlineContent || '').trim();
    if (outlineSrc) {
      const outlineRegex = /^[-*]\s*主题\s*\d*\s*[：:]\s*(.+)$/gm;
      let om: RegExpExecArray | null;
      while ((om = outlineRegex.exec(outlineSrc)) !== null) {
        const t = om[1].trim().replace(/^[*#>\s]+|[*#>\s]+$/g, '').trim();
        // 过滤占位/运营类条目（"（可替换）"、"（可选）"、复盘预告等），
        // 只保留核心系列标题（用户认可的 10 条：认知铺垫→动手搭建→打通铁律→越用越聪明→系统串联）
        if (/（可替换）|（可选）|复盘|预告|轻量内容/.test(t)) continue;
        if (t.length >= 4 && t.length <= 80 && !topics.includes(t)) topics.push(t);
        if (topics.length >= 25) break;
      }
    }

    // B. 详细版【标题】（Hermes 输出，多格式兼容：同一行/换行/列表）
    if (topics.length === 0) {
      const titleRegex =
        /【(?:口播)?标题】[ \t]*\*{0,2}[ \t]*(.+?)\s*\*{0,2}\s*(?:\n|$)|(?:[-*]\s*\*\*标题\*\*[：:]\s*(.+?)(?:\n|$))/g;
      let m: RegExpExecArray | null;
      while ((m = titleRegex.exec(content)) !== null) {
        const raw = m[1] || m[2] || '';
        const t = raw.trim().replace(/^[*#>\s]+|[*#>\s]+$/g, '').trim();
        // 过滤 Hermes 开场白（"好的，以下为…"）和无意义行
        if (/^好的|^以下|^直接可用|^好的，|^为您/.test(t)) continue;
        if (t.length >= 4 && t.length <= 80 && !topics.includes(t)) topics.push(t);
        if (topics.length >= 25) break;
      }
    }

    // C. 兜底：无匹配时按天分节（## 周一（...））或 ## 标题行
    if (topics.length === 0) {
      const sections = content.split(/^#{2,3}\s+/m);
      for (const sec of sections) {
        const header = sec.split('\n')[0]?.trim() || '';
        // 过滤"周一（日期）"这类纯日期节头，避免当成主题
        if (/^周[一二三四五六日]/.test(header)) continue;
        if (header.length >= 4 && header.length <= 80 && !topics.includes(header)) topics.push(header);
        if (topics.length >= 25) break;
      }
    }
    // 再兜底：markdown 标题行（# 开头）
    if (topics.length === 0) {
      for (const line of content.split('\n')) {
        const t = line.replace(/^#{1,4}\s+/, '').trim();
        if (t && t.length >= 4 && t.length <= 80 && !topics.includes(t)) topics.push(t);
        if (topics.length >= 25) break;
      }
    }

    if (topics.length === 0) {
      this.logger.warn?.('[ContentPlanService] 详细内容中未解析到主题，无法生成计划');
      return [];
    }

    // 2. v6.2.25 每个平台都分配【全部标题】（内容矩阵），而非拆分配置：
    // 用户诉求：详细大纲的每条标题都配到每个品牌，各平台按各自规则写不同内容，
    // 全平台同步发布同一批内容（公众号发第1集、抖音也发第1集，但写法不同）。
    // 旧实现 allocateTopics 是"循环分池"→ 平台只拿到部分标题（公众号1-3集/抖音4-6集…），
    // 用户反馈"标题变少、逻辑不对"的根因。
    const platformOrder: SocialPlatform[] = [
      'wechat', 'wechat_subscription', 'douyin_enterprise', 'xiaohongshu', 'zhihu', 'csdn', 'douyin_personal', 'toutiao', 'bilibili', 'live_stream',
      'wechat_channels', 'wechat_moments',
    ];
    const items: ContentPlanItem[] = [];
    for (const platform of platformOrder) {
      topics.forEach((topic, ti) => {
        items.push({
          id: `plan-${date}-${platform}-${ti}`,
          platform,
          topic,
          contentType: this.getContentType(platform),
          priority: ti < 2 ? 'high' : ti < 6 ? 'medium' : 'low',
          knowledgeNote: detailPath,
          status: 'pending',
          date,
        });
      });
    }

    // 3. 覆盖写入计划文件
    const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${date}.md`;
    const markdown = this.serializePlanToMarkdown(items, date);
    try {
      await fetch(`/vault-api/write?path=${encodeURIComponent(planPath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: markdown }),
      });
    } catch (err) {
      this.logger.warn?.('[ContentPlanService] 从详细内容生成计划后写入 Vault 失败', err);
    }

    this.logger.log?.(`[ContentPlanService] 已从周度大纲详细内容生成计划，共 ${items.length} 条（${topics.length} 个主题）`);
    return items;
  }

  /**
   * v6.1.10 主题分配（全路径统一）：保证每个平台都分到目标数量的主题
   *
   * 策略：按平台顺序循环分配，主题池不足时循环复用（允许共用主题——
   * 一个主题多平台分发 = 内容矩阵，公众号文章/抖音脚本/朋友圈文案形式不同，
   * 同一主题完全合理）。修复：主题池不足时平台不再空缺（用户反馈
   * "抖音个人号/视频号/朋友圈没有内容"的根因）。
   */
  private allocateTopics(
    date: string,
    platformOrder: SocialPlatform[],
    pool: string[],
    knowledgeNote?: string,
    poolNotes?: string[]
  ): ContentPlanItem[] {
    const items: ContentPlanItem[] = [];
    if (pool.length === 0) return items;
    const countOf = (platform: SocialPlatform) => (platform === 'bilibili' || platform === 'live_stream') ? 2 : 3;
    let idx = 0;
    for (const platform of platformOrder) {
      const target = countOf(platform);
      for (let i = 0; i < target; i++) {
        const srcIdx = idx % pool.length; // 循环复用：不足时共用主题
        items.push({
          id: `plan-${date}-${platform}-${i}`,
          platform,
          topic: pool[srcIdx],
          contentType: this.getContentType(platform),
          priority: idx < 2 ? 'high' : idx < 6 ? 'medium' : 'low',
          knowledgeNote: poolNotes?.[srcIdx] || knowledgeNote,
          status: 'pending',
          date,
        });
        idx++;
      }
    }
    return items;
  }

  /** 从今日新闻摘要提取主题池（v6：供"换一篇/换选题"使用，与 derivePlanFromNewsDigest 同一数据源） */
  private async extractNewsTitles(date: string): Promise<string[]> {
    const possiblePaths = [
      `XEAOS/Daily/News-${date}.md`,
      `XEAOS/Daily/News-${date}`,
      `XEAOS/Daily/news-digest-${date}.md`,
    ];
    let newsContent = '';
    for (const p of possiblePaths) {
      try {
        const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(p)}`);
        if (resp.ok) {
          const result = await resp.json();
          if (result.success && result.content) {
            newsContent = result.content;
            break;
          }
        }
      } catch {
        // 继续
      }
    }
    if (!newsContent) return [];
    const titles: string[] = [];
    for (const line of newsContent.split('\n')) {
      // Top Stories 表格行 / 商业机会标题
      const m1 = line.match(/^\|\s*\d+\s*\|\s*\d+\/10\s*\|\s*[^|]*\|\s*\[([^\]]+)\]\([^)]*\)\s*\|/);
      const m2 = line.match(/^###\s+.{0,4}#\d+\s+(.+?)\s*$/);
      const title = (m1?.[1] || m2?.[1] || '').trim();
      if (title && title.length >= 6 && title.length <= 80 && !titles.includes(title)) {
        titles.push(title);
      }
      if (titles.length >= 20) break;
    }
    return titles;
  }

  /**
   * v6.1：把某条目的知乎问答模式/问题写回计划文件（配合前端"设为问题/切问答模式"）
   * 按 topic 匹配条目（与 updatePlanItemStatus 一致），保留 status/packageId 原值，
   * 仅更新 type/question 元数据。
   */
  async updatePlanItemQuestion(
    date: string,
    topic: string,
    itemType: 'article' | 'qa',
    question?: string
  ): Promise<boolean> {
    try {
      const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${date}.md`;
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(planPath)}`);
      if (!resp.ok) return false;
      const result = await resp.json();
      if (!result.success || !result.content) return false;

      const lines = String(result.content).split('\n');
      let updated = false;
      const nextLines = lines.map((line) => {
        const match = line.match(/^-\s*\[([^\]]+)\]\s*(.+?)(?:（关联：(.+?)）)?(?:\s*\{(.+?)\})?$/);
        if (!match) return line;
        const lineTopic = match[2].trim();
        const hit =
          lineTopic === topic ||
          (lineTopic.includes(topic) && topic.length >= 4) ||
          (topic.includes(lineTopic) && lineTopic.length >= 4);
        if (!hit) return line;
        updated = true;
        const knowledge = match[3] ? `（关联：${match[3]}）` : '';
        // v6.1.1 修复：设问题/切模式后 status 强制回 pending（旧 status:completed 会残留，
        // 导致刷新后条目仍显示旧内容、生成按钮不出现）
        const oldMeta = match[4]?.trim() || '';
        const pkgMatch = oldMeta.match(/packageId:([^,}]+)/);
        let metaParts = `status:pending`;
        if (pkgMatch) metaParts += `,packageId:${pkgMatch[1].trim()}`;
        metaParts += `,type:${itemType}`;
        if (question) metaParts += `,question:${encodeURIComponent(question)}`;
        return `- [${match[1].trim()}] ${lineTopic}${knowledge} {${metaParts}}`;
      });
      if (!updated) return false;

      const writeResp = await fetch(`/vault-api/write?path=${encodeURIComponent(planPath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: nextLines.join('\n') }),
      });
      const wr = await writeResp.json();
      return wr.success === true;
    } catch (err) {
      this.logger.warn('[ContentPlanService] 计划问题写回失败:', err);
      return false;
    }
  }

  /**
   * v6：为单个计划条目换一个选题（用户"未生成也能换一篇"诉求）
   * 主题池：今日新闻热点 → 知识库笔记 → 现有计划其他主题；排除当前主题
   * @returns 新主题；无候选返回 null
   */
  async switchTopicForItem(date: string, excludeTopic: string): Promise<string | null> {
    const pool: string[] = [];
    // 1. 今日新闻热点
    try {
      pool.push(...(await this.extractNewsTitles(date)));
    } catch { /* 忽略 */ }
    // 2. 知识库笔记主题
    if (pool.length < 3) {
      try {
        const items = await this.buildTodayPlanItems(date);
        pool.push(...items.map((i) => i.topic));
      } catch { /* 忽略 */ }
    }
    // 3. 现有计划其他主题
    try {
      const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${date}.md`;
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(planPath)}`);
      if (resp.ok) {
        const result = await resp.json();
        if (result.success && result.content) {
          const parsed = this.parsePlanFromMarkdown(result.content, date);
          pool.push(...parsed.map((p) => p.topic));
        }
      }
    } catch { /* 忽略 */ }

    const candidates = [...new Set(pool)].filter(
      (t) => t && t !== excludeTopic && t.length >= 4 && t.length <= 80
    );
    if (candidates.length === 0) return null;
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  /**
   * v6：把计划文件中某条目的主题替换为新主题（配合 switchTopicForItem）
   */
  async updatePlanItemTopic(date: string, oldTopic: string, newTopic: string): Promise<boolean> {
    try {
      const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${date}.md`;
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(planPath)}`);
      if (!resp.ok) return false;
      const result = await resp.json();
      if (!result.success || !result.content) return false;

      const lines = String(result.content).split('\n');
      let updated = false;
      const nextLines = lines.map((line) => {
        const match = line.match(/^-\s*\[([^\]]+)\]\s*(.+?)(?:（关联：(.+?)）)?(?:\s*\{(.+?)\})?$/);
        if (!match) return line;
        const lineTopic = match[2].trim();
        const hit =
          lineTopic === oldTopic ||
          (lineTopic.includes(oldTopic) && oldTopic.length >= 4) ||
          (oldTopic.includes(lineTopic) && lineTopic.length >= 4);
        if (!hit) return line;
        updated = true;
        const knowledge = match[3] ? `（关联：${match[3]}）` : '';
        const meta = match[4] ? ` {${match[4].trim()}}` : '';
        return `- [${match[1].trim()}] ${newTopic}${knowledge}${meta}`;
      });
      if (!updated) return false;

      const writeResp = await fetch(`/vault-api/write?path=${encodeURIComponent(planPath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: nextLines.join('\n') }),
      });
      const wr = await writeResp.json();
      return wr.success === true;
    } catch (err) {
      this.logger.warn('[ContentPlanService] 计划主题替换失败:', err);
      return false;
    }
  }

  /**
   * v6.31 单平台自定义标题：向今日计划文件追加一条自定义选题（只影响指定平台，不动其他平台）
   *
   * 用户诉求（2026-08-15）："OPC 创业到底是什么？怎么做好？"只想给抖音个人号写一篇，
   * 不要全平台生成——一鱼多吃已失效，各平台内容必须独立。
   * 区别于 regeneratePlanByTopic（全平台围绕主题重写），本方法只 append 一行到计划文件。
   */
  async addCustomPlanItem(date: string, platform: SocialPlatform, topic: string): Promise<ContentPlanItem | null> {
    const cleanTopic = (topic || '').trim();
    if (!cleanTopic || cleanTopic.length < 4) return null;
    try {
      const planPath = `10_Operations（运营层）/Content/每日自媒体计划-${date}.md`;
      const resp = await fetch(`/vault-api/read?path=${encodeURIComponent(planPath)}`);
      let content = '';
      if (resp.ok) {
        const r = await resp.json();
        if (r.success && r.content) content = String(r.content);
      }
      const cfg = PLATFORM_CONFIG[platform];
      const item: ContentPlanItem = {
        id: `plan-${date}-${platform}-custom-${Date.now()}`,
        platform,
        topic: cleanTopic.slice(0, 60),
        contentType: this.getContentType(platform),
        status: 'pending',
        date,
        priority: 'high',
      };
      const line = `- [${cfg.name}] ${item.topic}`;
      const nextContent =
        content.trim().length > 0
          ? `${content.trimEnd()}\n${line}\n`
          : `# 每日自媒体计划 - ${date}\n\n> 生成时间：${new Date().toISOString()}\n\n${line}\n`;
      const writeResp = await fetch(`/vault-api/write?path=${encodeURIComponent(planPath)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: nextContent }),
      });
      const wr = await writeResp.json();
      if (wr.success === true) {
        this.logger.log?.(`[ContentPlanService] 已追加自定义选题「${item.topic}」到 ${cfg.name}`);
        return item;
      }
      return null;
    } catch (err) {
      this.logger.warn('[ContentPlanService] 自定义选题追加失败:', err);
      return null;
    }
  }

  /** 从自媒体计划 Markdown 解析
   *
   * 读取持久化的 status 和 packageId，恢复生成状态。
   * 兼容旧格式（无花括号元数据时默认 status: 'pending'）。
   */
  private parsePlanFromMarkdown(content: string, date: string): ContentPlanItem[] {
    const items: ContentPlanItem[] = [];
    const lines = content.split('\n');

    for (const line of lines) {
      // 匹配格式：- [平台] 主题（关联：xxx.md）{status:completed,packageId:xxx}
      // 花括号部分可选，兼容旧格式
      const match = line.match(/^-\s*\[([^\]]+)\]\s*(.+?)(?:（关联：(.+?)）)?(?:\s*\{(.+?)\})?$/);
      if (match) {
        const platformStr = match[1].trim();
        const topic = match[2].trim();
        const knowledgeNote = match[3]?.trim();
        const metaStr = match[4]?.trim();

        // 解析元数据：status:completed,packageId:xxx
        let status: ContentPlanItem['status'] = 'pending';
        let packageId: string | undefined;
        let itemType: 'article' | 'qa' | undefined;
        let question: string | undefined;
        if (metaStr) {
          const statusMatch = metaStr.match(/status:(\w+)/);
          if (statusMatch) {
            const s = statusMatch[1];
            if (s === 'pending' || s === 'generating' || s === 'completed' || s === 'failed' || s === 'published') {
              status = s;
            }
          }
          const pkgMatch = metaStr.match(/packageId:([^,}]+)/);
          if (pkgMatch) {
            packageId = pkgMatch[1].trim();
          }
          // v6.1 知乎问答模式：type:qa / question:xxx 从计划文件恢复
          const typeMatch = metaStr.match(/type:(article|qa)/);
          if (typeMatch) itemType = typeMatch[1] as 'article' | 'qa';
          const qMatch = metaStr.match(/question:([^,}]+)/);
          if (qMatch) question = decodeURIComponent(qMatch[1].trim());
        }

        const platform = this.parsePlatform(platformStr);
        if (platform) {
          items.push({
            id: `plan-${date}-${platform}-${items.length}`,
            platform,
            topic,
            knowledgeNote,
            contentType: this.getContentType(platform),
            status,
            packageId,
            itemType,
            question,
            date,
            priority: items.length < 2 ? 'high' : items.length < 4 ? 'medium' : 'low',
          });
        }
      }
    }

    return items;
  }

  /** 从今日日计划派生自媒体计划 */
  private derivePlanFromDaily(dailyContent: string, date: string): ContentPlanItem[] {
    const items: ContentPlanItem[] = [];

    // 提取日计划中的任务主题
    const topicMatches = dailyContent.match(/\d+\.\s*\*\*([^*]+)\*\*/g) || [];
    const topics = topicMatches
      .map((m) => m.replace(/\d+\.\s*\*\*([^*]+)\*\*/, '$1').trim())
      .filter((t) => t.length > 4)
      .slice(0, 7);

    // 为每个主题分配到不同平台（含B站与每日直播、今日头条）
    const platforms: SocialPlatform[] = ['wechat', 'douyin_enterprise', 'xiaohongshu', 'zhihu', 'csdn', 'douyin_personal', 'toutiao', 'bilibili', 'live_stream', 'wechat_channels', 'wechat_moments'];
    for (let i = 0; i < topics.length && i < platforms.length; i++) {
      items.push({
        id: `plan-${date}-${platforms[i]}-${i}`,
        platform: platforms[i],
        topic: topics[i],
        contentType: this.getContentType(platforms[i]),
        status: 'pending',
        date,
        priority: i < 2 ? 'high' : i < 4 ? 'medium' : 'low',
      });
    }

    return items;
  }

  /** 默认计划模板 */
  private getDefaultPlan(date: string): ContentPlanItem[] {
    const defaultTopics = [
      { platform: 'wechat' as const, topic: 'AI 自动化运营的 5 个核心场景', priority: 'high' as const },
      { platform: 'douyin_enterprise' as const, topic: '企业如何用 AI 提效 30%', priority: 'high' as const },
      { platform: 'xiaohongshu' as const, topic: 'AI 工具推荐：提升工作效率的 10 款神器', priority: 'medium' as const },
      { platform: 'zhihu' as const, topic: 'AI Agent 与传统自动化的本质区别', priority: 'medium' as const },
      { platform: 'douyin_personal' as const, topic: '程序员的一天：用 AI 写代码', priority: 'low' as const },
      { platform: 'bilibili' as const, topic: '从 0 搭建 AI OS：我的真实开发记录', priority: 'low' as const },
      { platform: 'live_stream' as const, topic: '今日AI速递：直播解读行业动态与实战案例', priority: 'high' as const },
    ];

    return defaultTopics.map((t, i) => ({
      id: `plan-${date}-${t.platform}-${i}`,
      platform: t.platform,
      topic: t.topic,
      contentType: this.getContentType(t.platform),
      status: 'pending' as const,
      date,
      priority: t.priority,
    }));
  }

  /** 根据平台推断内容类型 */
  private getContentType(platform: SocialPlatform): 'article' | 'video' | 'image_text' | 'live' {
    // 文章类：微信公众号、知乎、今日头条、CSDN（头条图文为主，标题/首图权重高）
    if (platform === 'wechat' || platform === 'zhihu' || platform === 'toutiao' || platform === 'csdn') return 'article';
    // 图文类：小红书、朋友圈（短文案+配图）
    if (platform === 'xiaohongshu' || platform === 'wechat_moments') return 'image_text';
    // 直播类：每日直播
    if (platform === 'live_stream') return 'live';
    // 视频类：抖音企业号/抖音个人号/B站/微信视频号
    return 'video';
  }

  /** 解析平台字符串 */
  private parsePlatform(str: string): SocialPlatform | null {
    const lower = str.toLowerCase();
    // v6.1.18 修复：特例必须排在'微信'之前——"微信视频号/微信朋友圈"也包含"微信"，
    // 旧顺序被第 1061 行 intercept 成 wechat → 视频号/朋友圈条目全部错分到微信公众号分组
    // （用户反馈"视频号和朋友圈还是没有内容"的根因：内容其实都在公众号分组里）
    if (lower.includes('视频号') || lower.includes('wechat_channels') || lower.includes('channels')) return 'wechat_channels';
    if (lower.includes('朋友圈') || lower.includes('wechat_moments') || lower.includes('moments')) return 'wechat_moments';
    // v6.31 修复：订阅号「小舍AI」热度号缺失 → parsePlatform 返回 null → 条目被丢弃
    // （与今日头条同款平台解析遗漏；必须排在 wechat 判断之前，否则 wechat_subscription 被 wechat 截获）
    if (lower.includes('订阅号') || lower.includes('wechat_subscription')) return 'wechat_subscription';
    if (lower.includes('微信') || lower.includes('wechat') || lower.includes('公众号')) return 'wechat';
    if (lower.includes('抖音企业') || lower.includes('douyin_enterprise')) return 'douyin_enterprise';
    if (lower.includes('抖音个人') || lower.includes('douyin_personal')) return 'douyin_personal';
    if (lower.includes('小红书') || lower.includes('xiaohongshu') || lower.includes('xhs')) return 'xiaohongshu';
    if (lower.includes('知乎') || lower.includes('zhihu')) return 'zhihu';
    // v6.7 CSDN：技术博客（注意：需在 zhihu 之后判断，避免误匹配；无歧义关键词 'csdn'）
    if (lower.includes('csdn')) return 'csdn';
    // v6.31 修复：今日头条平台缺失 → parsePlatform 返回 null → [今日头条] 条目被丢弃，
    // 内容工厂"今日头条分组永远为空"（与 v6.1.18 视频号/朋友圈同款平台解析遗漏 bug）
    if (lower.includes('头条') || lower.includes('toutiao')) return 'toutiao';
    if (lower.includes('b站') || lower.includes('bilibili') || lower.includes('哔哩')) return 'bilibili';
    if (lower.includes('直播') || lower.includes('live_stream') || lower.includes('live')) return 'live_stream';
    return null;
  }
}

/** 单例 */
let contentPlanInstance: ContentPlanService | null = null;

export function getContentPlanService(): ContentPlanService {
  if (!contentPlanInstance) {
    contentPlanInstance = new ContentPlanService();
  }
  return contentPlanInstance;
}

export default ContentPlanService;
